import {
  AbsoluteCenter,
  Box,
  chakra,
  Checkbox,
  Flex,
  IconButton,
  Image,
  shouldForwardProp,
  Text,
  Spinner,
} from "@chakra-ui/react";
import React, { useEffect, useState, useRef } from "react";
import { COMMON_WEB_PATH } from "../../constant";
import { useDispatch, useSelector } from "react-redux";
import { toggleBGM } from "../../redux/action/action";
import MyBalance from "../Balance/BalanceAmount";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isValidMotionProp, motion } from "framer-motion";
import { IoClose } from "react-icons/io5";
import i18next from "i18next";
import CommonModal from "../Common/CommonModal";
import ClearCashButTransfer from "../ClearCash/ClearCashButTransfer";
import html2canvas from "html2canvas";
import { QRCodeCanvas } from "qrcode.react";
import { appName } from "../../config";

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const EndGameDetail = ({
  onClose,
  previousBalance,
  transCode,
  withdrawalNumber,
}) => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);

  const [isClosing, setIsClosing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(true);
  const [capturedImage, setCapturedImage] = useState(null);

  const memberInfo = useSelector((state) => state.isMemberInfo);
  const [currentTime] = useState(dayjs().format("YYYY-MM-DD HH:mm:ss"));
  const navigate = useNavigate();

  const isBGMEnabled = useSelector((state) => state.isBGMEnabled);
  const dispatch = useDispatch();

  const captureRef = useRef(null);

  // 處理確認按鈕
  const handleConfirm = () => {
    setIsClosing(true);
    dispatch(
      toggleBGM({
        ...isBGMEnabled,
        song: "back2_",
        musicVolume: 0.2,
      }),
    );
    navigate("/");
    onClose();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (captureRef.current) {
        html2canvas(captureRef.current, {
          backgroundColor: null,
          useCORS: true,
          scale: 2,
          onclone: (clonedDoc) => {
            // 處理 html2canvas 不支援 background-clip: text 的問題
            const clonedElement = clonedDoc.getElementById("result-amount");
            if (clonedElement) {
              clonedElement.style.background = "none";
              clonedElement.style.color = "#fdd26a";
              clonedElement.style.webkitTextFillColor = "#fdd26a";
            }
          },
        })
          .then((canvas) => {
            const base64Image = canvas.toDataURL("image/png");
            setCapturedImage(base64Image);
            setIsGenerating(false);
          })
          .catch((err) => {
            console.error("Capture failed:", err);
            setIsGenerating(false);
          });
      }
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const resultAmount = previousBalance || 0;

  return (
    <CommonModal>
      {/* 1. 隱藏的擷取區域 (Snapshot Area) */}
      <Box
        ref={captureRef}
        position="absolute"
        left="-9999px"
        top="-9999px"
        w="375px" // 模擬移動端寬度以確保排版一致
        bg="transparent"
      >
        <Flex
          flexDirection="column"
          gap={2}
          justifyContent="center"
          w="100%"
          position="relative"
        >
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            mb={5}
          >
            <Flex
              justifyContent="center"
              alignItems="center"
              fontSize="24px"
              color="white"
              textAlign="center"
              gap={4}
              bgImage={`url("${COMMON_WEB_PATH}/theOne/modal/word_background.png")`}
              bgSize="100%100%"
              bgRepeat="no-repeat"
              bgPosition="center"
              w="90%"
              py="10px"
            >
              {i18n("machineCode")}
              <Text color="#fff" fontWeight="700">
                {"<NO."}
                {memberInfo?.uid || "-"}
                {">"}
              </Text>
            </Flex>
            <Box fontSize="20px" textAlign="center" mt={1}>
              <Text color="#C5B3C7" fontWeight="500">
                {currentTime}
              </Text>
            </Box>
            <Box fontSize="20px" textAlign="center">
              <Flex gap={2} color="#C5B3C7" fontWeight="500">
                <span>{i18n("withdrawNumber")}:</span>
                {withdrawalNumber || "-"}
              </Flex>
            </Box>
          </Flex>

          <Flex
            bgImage={`url("${COMMON_WEB_PATH}/theOne/modal/word_background.png")`}
            bgSize="100%100%"
            bgRepeat="no-repeat"
            bgPosition="center"
            w="90%"
            justifyContent="center"
            alignItems="center"
            fontSize="24px"
            color="white"
            textAlign="center"
            mx="auto"
          >
            <Text>{i18n("GAME RESULT")}</Text>
          </Flex>

          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            fontSize="32px"
            color="white"
            textAlign="center"
            position="relative"
          >
            {/* 浮水印層 */}
            <Text
              position="absolute"
              zIndex={1}
              opacity={0.05}
              fontStyle="italic"
              color="white"
              pointerEvents="none"
              bottom="50%"
              transform="rotate(-15deg)"
            >
              {appName}-{i18n("棄分")}:{resultAmount}
            </Text>

            <Text
              id="result-amount"
              color="#fdd26a"
              fontWeight="700"
              fontSize="36px"
              zIndex={2}
              mb={5}
            >
              {resultAmount}
            </Text>
          </Flex>
          {/* 留空給按鈕位置，確保排版與顯示層一致 */}
        </Flex>
      </Box>

      {/* 2. 顯示層 (Display Layer) */}
      <Flex
        flexDirection="column"
        gap={2}
        justifyContent="center"
        position="relative"
        zIndex={2}
        w="100%"
      >
        {isGenerating ? (
          <AbsoluteCenter>
            <Flex direction="column" align="center" gap={4}>
              <Spinner size="xl" color="brand.500" />
              <Text color="white">{i18n("Generating results...")}</Text>
            </Flex>
          </AbsoluteCenter>
        ) : (
          <Image
            src={capturedImage}
            alt="Game Result"
            w="100%"
            borderRadius="md"
          />
        )}
        {/* 4. QRCode 驗證區域 */}
        {!isGenerating && withdrawalNumber && (
          <Flex
            direction="column"
            align="center"
            gap={2}
            mt={1}
            pb={4}
            zIndex={2}
            position="relative"
          >
            <Box p={2} bg="white" borderRadius="md">
              <QRCodeCanvas
                value={`${window.location.origin}/cash-register/order/${withdrawalNumber}`}
                size={90}
              />
            </Box>
            <Text color="white" fontSize="xs" opacity={0.8}>
              {i18n("orderVerification")}
            </Text>
          </Flex>
        )}

        {/* 3. 互動層 (Interactive Layer) */}
        {!isGenerating && (
          <Flex
            zIndex={3}
            direction="column"
            alignItems="center"
            gap={6}
            w="100%"
          >
            <Flex
              as="button"
              w="165px"
              h="62px"
              bgImage={`url('${COMMON_WEB_PATH}/theOne/button/confirm_background.png')`}
              bgPosition="center"
              bgRepeat="no-repeat"
              bgSize="100% 100%"
              cursor="pointer"
              justifyContent="center"
              alignItems="center"
              onClick={handleConfirm}
              _hover={{ filter: "brightness(1.1)" }}
            >
              <Text mb="5px" color="#0C5426" fontWeight={900} fontSize="23px">
                {i18n("confirm")}
              </Text>
            </Flex>
          </Flex>
        )}
      </Flex>

      {/* 隱形關閉按鈕 */}
      <Box
        position="absolute"
        top="0"
        right="0"
        w="60px"
        h="30px"
        onClick={handleConfirm}
        cursor="pointer"
        zIndex={10}
      ></Box>

      {/* QRcode遮罩/原有清除現金邏輯 */}
      <ClearCashButTransfer onCancelClearCash={onClose} transCode={transCode} />
    </CommonModal>
  );
};

export default EndGameDetail;
